import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useOrderQuery, useOrderFromList } from '#/api/queries/orderQueries';
import SpinningRoundedBorder from '#/components/basic/animations/SpinningRoundedBorder';
import Button from '#/components/basic/Button/Button';
import ButtonRouterLink from '#/components/basic/Button/ButtonRouterLink';
import RouterLink from '#/components/basic/Link/RouterLink';
import SimpleModalOrMobilePage from '#/components/basic/modal/SimpleModalOrMobilePage';
import DataPair from '#/components/basic/panels/DataPair';
import ErrorPanel from '#/components/basic/panels/ErrorPanel';
import TextColored from '#/components/basic/text/TextColored';
import { useSelectorActiveChain } from '#/components/hooks';
import TPSLOrderColumnWrap from '#/components/positions/takeProfitStopLoss/TPSLOrderColumnWrap';
import { MobileDataPairCoinIcon } from '#/config/coinIcons/CoinIcon';
import { EXCHANGE_DECIMALS } from '#/config/constants';
import { formatDateTime } from '#/lib/date';
import { formatPrice } from '#/lib/display/formatting';
import { capitalizeFirstLetter } from '#/lib/string';
import {
  getDashboardUrl,
  useSelectMarketStepSize,
  useSelectMarketTickSize,
} from '#/reduxState/markets/listed/listedMarketsUtils';
import { useSelector } from '#/reduxState/store';

import {
  cancelOrderWithSignModal,
  prepareCancelErrorMessage,
} from '../orderCancelUtils';
import {
  getOrderQuantityWithSymbol,
  isMarketOrder,
  getOrderExecutedQuantityWithSymbol,
  getOrderTotalInQuoteWithSymbol,
  isOrderPostOnly,
  getOrderTriggerExplanation,
  getReadableOrderType,
} from '../orderUtil';

import OrderDetailFills from './OrderDetailFills';

const ContentWrap = styled.div`
  flex: 1;
  padding: 0 0 12px;
`;

const SpinnerWrap = styled.div`
  display: flex;
  align-items: center;
  .isDesktopStyle && {
    min-height: 400px;
  }
`;

const ExecutedTradesTitle = styled.div`
  padding-top: 20px;
  color: var(--gray4c);
  font-size: var(--small);
`;

const NoTradesWrap = styled.div`
  margin: 8px 0;
  padding: 16px;
  border-radius: 5px;
  border: 1px solid var(--gray0_separator);
  color: var(--gray4c);
  text-align: center;
  font-size: var(--medium);
  line-height: normal;
`;

type Props = {
  orderUuid: string;
  highlightedFillId?: string;
  onClose: () => void;
  showingOn: 'trade-history' | 'open-orders' | 'dashboard';
};

/** Order detail Modal (desktop) or Page (mobile) */
export default function OrderDetailScreen({
  orderUuid,
  highlightedFillId,
  onClose,
  showingOn,
}: Props) {
  const wallet = useSelector(state => state.wallet);
  const delegatedKey = useSelector(state => state.delegatedKey.authorization);
  const maximumPositionSizes = useSelector(
    state => state.markets.listedMarkets.maximumPositionSizes
  );
  const { activeChain } = useSelectorActiveChain();
  const [canceling, setCanceling] = useState(false);
  const [cancelErrorMessage, setCancelErrorMessage] = useState('');

  const { data: orderWithoutFills } = useOrderFromList(orderUuid);
  const { data: orderAllData } = useOrderQuery(orderUuid);

  // Make loading of active orders instant (we only wait for fills)
  const order = orderAllData || orderWithoutFills;

  const orderFillsDESC = useMemo(
    () =>
      orderAllData ? orderAllData?.fills?.slice().reverse() || [] : undefined,
    [orderAllData]
  );

  const tickSizeDecimals = useSelectMarketTickSize(order?.market)
    ?.visibleDecimals;
  const stepSizeDecimals = useSelectMarketStepSize(order?.market)
    ?.visibleDecimals;
  const isCancellable =
    order?.status === 'active' ||
    order?.status === 'open' ||
    order?.status === 'partiallyFilled';

  const maximumPositionSize = order
    ? maximumPositionSizes[order.market]
    : undefined;

  const totalValue = order
    ? getOrderTotalInQuoteWithSymbol(order, maximumPositionSize)
    : undefined;

  let bgSequence = 1;
  /** Guarantee that each 2nd will have darker backgound through single render */
  const lightBgSequence = () => bgSequence++ % 2 === 0;

  return (
    <SimpleModalOrMobilePage
      title="Order Details"
      onClose={onClose}
      size="small"
      verticalAlignOnMobile="centered"
    >
      {!order ? (
        <SpinnerWrap>
          <SpinningRoundedBorder />
        </SpinnerWrap>
      ) : (
        <div>
          <ContentWrap>
            <DataPair label="Date" altBackground={lightBgSequence()}>
              {order && formatDateTime(order.time, { seconds: true })}
            </DataPair>

            <DataPair
              label="Market"
              altBackground={lightBgSequence()}
              childrenFontFamily="standard"
            >
              <RouterLink
                to={getDashboardUrl(
                  activeChain,
                  `${order?.baseSymbol}-${order?.quoteSymbol}`
                )}
              >
                {order?.baseSymbol && (
                  <MobileDataPairCoinIcon symbol={order?.baseSymbol} />
                )}
                {order?.baseSymbol}-{order?.quoteSymbol}
              </RouterLink>
            </DataPair>

            <DataPair
              label="Type"
              altBackground={lightBgSequence()}
              childrenFontFamily="standard"
            >
              {getReadableOrderType(order?.type)}
            </DataPair>

            <DataPair
              label="Side"
              altBackground={lightBgSequence()}
              childrenFontFamily="standard"
            >
              <TextColored color={order?.side === 'buy' ? 'green' : 'red'}>
                {capitalizeFirstLetter(order?.side)}
              </TextColored>
            </DataPair>

            {!order ? (
              <DataPair label="Price" altBackground={lightBgSequence()}>
                {' '}
                {/* We want to show price when loading detail - hide if it is order in quote terms (market buy) */}
              </DataPair>
            ) : !isMarketOrder(order.type) ? (
              <DataPair label="Price" altBackground={lightBgSequence()}>
                {order.price ? formatPrice(order.price, tickSizeDecimals) : ''}
              </DataPair>
            ) : order.avgExecutionPrice ? (
              <DataPair label="Average Price" altBackground={lightBgSequence()}>
                {formatPrice(order.avgExecutionPrice, tickSizeDecimals)}{' '}
                {order.quoteSymbol}
              </DataPair>
            ) : null}

            {order?.triggerPrice && (
              <DataPair label="Trigger Price" altBackground={lightBgSequence()}>
                {formatPrice(order.triggerPrice, tickSizeDecimals)}{' '}
                {order.quoteSymbol}
              </DataPair>
            )}

            <DataPair label="Quantity" altBackground={lightBgSequence()}>
              {order &&
                getOrderQuantityWithSymbol(
                  order,
                  stepSizeDecimals,
                  maximumPositionSize
                )}
            </DataPair>

            {totalValue && (
              <DataPair label="Value" altBackground={lightBgSequence()}>
                {totalValue}
              </DataPair>
            )}

            <DataPair label="Filled" altBackground={lightBgSequence()}>
              {order &&
                getOrderExecutedQuantityWithSymbol(order, stepSizeDecimals)}
            </DataPair>

            <DataPair
              label="Reduce-Only"
              altBackground={lightBgSequence()}
              childrenFontFamily="standard"
            >
              {order ? (order?.reduceOnly ? 'Yes' : 'No') : ''}
            </DataPair>

            <DataPair
              label="Post-Only"
              altBackground={lightBgSequence()}
              childrenFontFamily="standard"
            >
              {order ? (isOrderPostOnly(order) ? 'Yes' : 'No') : ''}
            </DataPair>

            {order?.triggerPrice && (
              <DataPair
                label="Trigger Conditions"
                altBackground={lightBgSequence()}
              >
                {getOrderTriggerExplanation(order, tickSizeDecimals)}
              </DataPair>
            )}

            <DataPair label="TP/SL" altBackground={lightBgSequence()}>
              <TPSLOrderColumnWrap order={order} />
            </DataPair>

            <ExecutedTradesTitle>EXECUTED TRADES</ExecutedTradesTitle>

            {!orderFillsDESC ? (
              // Loading...
              <SpinningRoundedBorder size={40} />
            ) : orderFillsDESC.length === 0 ? (
              <NoTradesWrap>No executed trades</NoTradesWrap>
            ) : (
              <OrderDetailFills
                orderFills={orderFillsDESC}
                baseSymbol={order.baseSymbol}
                quoteSymbol={order.quoteSymbol}
                highlightedFillId={highlightedFillId}
                tickSizeDecimals={tickSizeDecimals ?? EXCHANGE_DECIMALS}
                stepSizeDecimals={stepSizeDecimals ?? EXCHANGE_DECIMALS}
                activeChain={activeChain}
              />
            )}
          </ContentWrap>
          {cancelErrorMessage && <ErrorPanel message={cancelErrorMessage} />}
          {isCancellable ? (
            <Button
              disabled={canceling}
              color="red"
              bordered
              onClick={() => {
                setCanceling(true);
                cancelOrderWithSignModal(orderUuid, wallet, delegatedKey)
                  .then(() => {
                    console.log('Order canceled', orderUuid);
                    onClose();
                  })
                  .catch(error =>
                    setCancelErrorMessage(prepareCancelErrorMessage(error))
                  );
              }}
            >
              Cancel Order
            </Button>
          ) : (
            showingOn === 'trade-history' && (
              <ButtonRouterLink
                to={getDashboardUrl(
                  activeChain,
                  `${order.baseSymbol}-${order.quoteSymbol}`
                )}
              >
                View Market
              </ButtonRouterLink>
            )
          )}
        </div>
      )}
    </SimpleModalOrMobilePage>
  );
}
